import React, {useRef} from 'react'
import {Icon, IconName} from '@dv5/components'
import {LayoutViewMode} from '@dv5/domain'
import {useHoverMenu} from '../../../components'
import './ExpandCollapseButton.less'

interface ExpandCollapseButtonProps {
    viewMode?: LayoutViewMode
    onCycle?: () => void
    onSetMode?: (mode: LayoutViewMode) => void
    isExpanded?: boolean
    onToggle?: () => void
    tooltipTextExpanded?: string
    tooltipTextCollapsed?: string
    className?: string
    onShowPopup?: (isVisible: boolean) => void
    iconColor?: string
}

const MODE_LABELS: Record<LayoutViewMode, string> = {
    [LayoutViewMode.NORMAL]: 'Normal Mode',
    [LayoutViewMode.FOCUS]: 'Focus Mode',
    [LayoutViewMode.FULL_PAGE]: 'Full Page Mode',
}

export const ExpandCollapseButton: React.FC<ExpandCollapseButtonProps> = ({
                                                                              viewMode,
                                                                              onCycle,
                                                                              onSetMode,
                                                                              isExpanded,
                                                                              onToggle,
                                                                              tooltipTextExpanded = 'Collapse',
                                                                              tooltipTextCollapsed = 'Expand',
                                                                              className = '',
                                                                              onShowPopup,
                                                                              iconColor,
                                                                          }) => {
    const {showPopup, setShowPopup, onMouseEnter, onMouseLeave} = useHoverMenu({
        onShowPopup,
    })
    const buttonRef = useRef<HTMLButtonElement>(null)
    const popupRef = useRef<HTMLDivElement>(null)

    const isLayoutMode = viewMode !== undefined && (onCycle !== undefined || onSetMode !== undefined)
    const isSimpleMode = isExpanded !== undefined && onToggle !== undefined

    const handleClick = () => {
        if (isLayoutMode) {
            if (onSetMode) {
                if (viewMode === LayoutViewMode.NORMAL) {
                    onSetMode(LayoutViewMode.FOCUS)
                } else if (viewMode === LayoutViewMode.FOCUS) {
                    onSetMode(LayoutViewMode.FULL_PAGE)
                } else {
                    onSetMode(LayoutViewMode.NORMAL)
                }
            } else if (onCycle) {
                onCycle()
            }
        } else if (isSimpleMode && onToggle) {
            onToggle()
        }
    }

    const getIconName = (): IconName => {
        if (isLayoutMode && viewMode !== undefined) {
            switch (viewMode) {
                case LayoutViewMode.NORMAL:
                    return IconName.Expand
                case LayoutViewMode.FOCUS:
                    return IconName.Expand
                case LayoutViewMode.FULL_PAGE:
                    return IconName.Collapse
            }
        }
        return isExpanded ? IconName.Collapse : IconName.Expand
    }

    const getTooltipText = (): string => {
        if (isLayoutMode && viewMode !== undefined) {
            switch (viewMode) {
                case LayoutViewMode.NORMAL:
                    return 'Switch to Focus Mode'
                case LayoutViewMode.FOCUS:
                    return 'Switch to Full Page Mode'
                case LayoutViewMode.FULL_PAGE:
                    return 'Switch to Normal Mode'
            }
        }
        return isExpanded ? tooltipTextExpanded : tooltipTextCollapsed
    }

    const handleModeSelect = (event: React.MouseEvent<HTMLDivElement>, mode: LayoutViewMode) => {
        event.stopPropagation()
        event.preventDefault()
        setShowPopup(false)
        if (onSetMode) {
            onSetMode(mode)
        } else if (onCycle) {
            onCycle()
        }
    }

    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
                setShowPopup(false)
            }
        }

        if (showPopup) {
            document.addEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showPopup, setShowPopup])

    const iconName = getIconName()
    const tooltipText = getTooltipText()

    return (
        <div className={`expand-collapse-button-container ${className}`}>
            <button
                ref={buttonRef}
                className="expand-collapse-button"
                style={iconColor ? {color: iconColor} : undefined}
                onClick={handleClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                aria-label={tooltipText}
                title={tooltipText}
            >
                <Icon
                    name={iconName}
                    className="expand-collapse-icon"
                    width="20"
                    height="20"
                />
            </button>

            {isLayoutMode && showPopup && (
                <div ref={popupRef} className="expand-collapse-popup">
                    {Object.values(LayoutViewMode).map(mode => (
                        <div
                            key={mode}
                            className={`expand-collapse-popup-item${mode === viewMode ? ' active' : ''}`}
                            onMouseDown={event => handleModeSelect(event, mode)}
                        >
                            {MODE_LABELS[mode]}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
